import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Leaf, Droplets, HandHeart } from "lucide-react";
import api from "../lib/api";
import { MaskLines, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";
import { MANIFESTO, INGREDIENTS, BRAND, GLOSSARY_SUBHEAD } from "../data/content";

const HERO_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_radiant-skincare-15/artifacts/197489cj_IMG_2924.jpeg";
const ORIGIN_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_radiant-skincare-15/artifacts/mdv8res3_cured_clear_body_butter_website.webp";
const LIFESTYLE_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_radiant-skincare-15/artifacts/nomcj7ht_ruths-glow-website-high-quality.webp";

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.12]);

  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-cream grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Rich & Radiant Kpangnan body butter, open jar" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-cream/70 via-cream/60 to-cream" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 sm:px-8 pb-16 pt-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6 font-mono text-xs uppercase tracking-[0.28em] text-gold"
        >
          Ingredient-First Body Butter · Rare natural butters sourced from Africa
        </motion.p>

        <h1 className="font-serif text-[13vw] leading-[0.92] text-moss sm:text-[10vw] lg:text-[8.5vw] font-light">
          <MaskLines lines={["Feed your skin", "what it"]} delay={0.25} />
          <span className="italic text-gold">
            <MaskLines lines={["actually needs."]} delay={0.55} />
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md text-muted leading-relaxed">
            Rare natural butters sourced from Africa, whipped with intention. No fillers. No shortcuts. Quality you can read on
            the label and feel on your skin.
          </p>
          <div className="flex gap-4">
            <Link
              to="/shop"
              className="btn-gold group flex items-center gap-2 rounded-sm px-7 py-4 text-sm font-semibold uppercase tracking-wider"
              data-testid="hero-shop-cta"
            >
              Shop the Butters
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/about"
              className="rounded-sm border border-moss/40 px-7 py-4 text-sm font-semibold uppercase tracking-wider text-moss transition-colors hover:bg-moss hover:text-cream"
              data-testid="hero-about-cta"
            >
              Our Story
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function StatementSection() {
  return (
    <section className="bg-cream py-24 sm:py-32 grain relative overflow-hidden" data-testid="brand-statement">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-8">The Cured & Clear Difference</p>
          <h2 className="max-w-5xl font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.06] text-moss" data-testid="brand-statement-headline">
            Most brands lead with scent. <span className="italic text-gold">We lead with ingredients.</span>
          </h2>
          <p className="mt-8 font-serif text-2xl sm:text-3xl italic text-moss">There is a difference.</p>
          <p className="mt-6 max-w-xl text-muted leading-relaxed">
            Every ingredient in a Cured & Clear jar earns its place by doing something real for your skin. No perfume
            padding. No fillers. Just rare natural butters, whipped with intention.
          </p>
          <Link
            to="/glossary"
            className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-moss link-underline"
            data-testid="brand-statement-glossary-link"
          >
            Meet every ingredient <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

const PILLARS = [
  { icon: Leaf, title: "Rare Butters", text: "Kpangnan and Nilotica shea — rare natural butters sourced from Africa." },
  { icon: Droplets, title: "Non-Greasy Glow", text: "Fast-absorbing, deeply hydrating, and never greasy." },
  { icon: HandHeart, title: "Sourced Right", text: "Traditional methods, women-led cooperatives, real integrity." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Hero />
      <Marquee />

      {/* pillars */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 grid gap-8 md:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <div className="flex gap-4">
              <p.icon className="h-7 w-7 shrink-0 text-gold" />
              <div>
                <h3 className="font-serif text-2xl text-moss">{p.title}</h3>
                <p className="mt-1 text-sm text-muted leading-relaxed">{p.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <StatementSection />

      {/* origin / brand */}
      <section className="bg-cream-2 py-20 sm:py-28" data-testid="home-origin">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <div className="mx-auto max-w-[880px] overflow-hidden rounded-sm border border-[#E4DDD0] bg-cream shadow-sm">
              <img
                src={ORIGIN_IMG}
                alt="Hand-carved African statues with shea nuts and Cured & Clear body butter"
                className="h-auto w-full"
                data-testid="home-origin-image"
              />
            </div>
            <div className="mt-12 max-w-3xl">
              <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-5">Sourced With Integrity</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-moss leading-[1.05]">Rooted in tradition, backed by nature.</h2>
              <p className="mt-6 text-muted leading-relaxed">{BRAND.about}</p>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-moss link-underline" data-testid="home-about-link">
                Read our story <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ingredient education */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24" data-testid="home-ingredients">
        <Reveal>
          <div className="mb-12 border-b border-[#E4DDD0] pb-6">
            <div className="flex items-end justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-3">Know Every Ingredient</p>
                <h2 className="font-serif text-4xl sm:text-5xl text-moss">Meet the <span className="italic text-gold">butters and oils.</span></h2>
              </div>
              <Link to="/glossary" className="hidden sm:flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-moss link-underline" data-testid="home-glossary-link">
                Full glossary <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <p className="mt-4 max-w-2xl text-muted leading-relaxed">{GLOSSARY_SUBHEAD}</p>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {INGREDIENTS.map((ing, i) => (
            <Reveal key={ing.name} delay={i * 0.1}>
              <div className="h-full rounded-sm border border-[#E4DDD0] bg-cream p-8 transition-colors hover:border-gold">
                <p className="font-mono text-[10px] uppercase tracking-wider text-gold">{ing.tag}</p>
                <h3 className="mt-3 font-serif text-3xl text-moss">{ing.name}</h3>
                <p className="mt-1 font-mono text-xs italic text-muted">{ing.latin}</p>
                <p className="mt-4 text-sm text-muted leading-relaxed">{ing.intro}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* products */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24" data-testid="home-products">
        <Reveal>
          <div className="flex items-end justify-between mb-12 border-b border-[#E4DDD0] pb-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-3">The Collection</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-moss">Two butters. Zero compromise.</h2>
            </div>
            <Link to="/shop" className="hidden sm:flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-moss link-underline" data-testid="home-shop-all">
              Shop all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid gap-10 sm:grid-cols-2 lg:gap-16">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* lifestyle feature */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28" data-testid="lifestyle-banner">
        <Reveal>
          <div className="mx-auto max-w-[1000px] overflow-hidden rounded-sm border border-[#E4DDD0] shadow-sm">
            <motion.img
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              src={LIFESTYLE_IMG}
              alt="Ruth's Glow body butter jars styled with a carved elephant and white blossoms"
              className="h-auto w-full"
              data-testid="lifestyle-banner-image"
            />
          </div>
          <div className="mt-12 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-4">No Fillers. No Shortcuts.</p>
            <p className="font-serif text-3xl sm:text-5xl italic text-moss leading-[1.1]" data-testid="lifestyle-banner-text">
              Quality you can read on the label.
            </p>
          </div>
        </Reveal>
      </section>

      {/* manifesto */}
      <section className="bg-cream-2 grain relative py-24 sm:py-32" data-testid="home-manifesto">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-4">The Manifesto</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-16 max-w-2xl leading-[1.05] text-moss">
              Three beliefs we refuse to compromise.
            </h2>
          </Reveal>
          <div className="space-y-0">
            {MANIFESTO.map((c, i) => (
              <Reveal key={c.chapter} delay={i * 0.05}>
                <div className="grid gap-6 border-t border-[#E4DDD0] py-10 md:grid-cols-[auto_1fr_1.4fr] md:items-start md:gap-12">
                  <span className="font-serif text-5xl text-gold">{c.chapter}</span>
                  <div>
                    <h3 className="font-serif text-3xl text-moss">{c.title}</h3>
                    <p className="mt-2 font-serif text-xl italic text-gold">{c.quote}</p>
                  </div>
                  <p className="text-muted leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />
    </div>
  );
}
